import path from 'path'
import { EventEmitter } from 'eventemitter3'
import moment from 'moment/moment'
import 'moment/locale/hu'
import numeral from 'numeral'
import * as versions from 'compare-versions'
import config from '../config'

const Anvil = new class extends EventEmitter {

	constructor () {
		super()
	}

	/*
	 * Load all optional dependencies, controllers and models - executed once on startup
	 */
	async load (controllerGlob, modelGlob, viewGlob) {

		// Global constants
		Anvil.pages = []			// Page stack
		Anvil.page = {}				// Current page data cache - emptied every time a new page is rendered successfully
		Anvil.modal_open = false	// Whether or not a modal window is already open (no stacking!)
		Anvil.components = {}		// Component index, all active components are referenced here

		Anvil.route_map = new Map()
		Anvil.controller_map = new Map()
		Anvil.model_map = new Map()
		Anvil.view_map = new Map()

		Anvil.controllers = {}
		Anvil.models = {}

		Anvil.fullscreen = false
		Anvil.cache = {}			// An object that you can use as you see fit (instead of global scope)
		Anvil._spinner_jobs = 0

		// Fetch configuration
		Anvil.config = config

		// Load languages
		/*
		Anvil.languages = 'TODO'
		Anvil.t = {}	// Active language
		*/

		// Crypto — use the native Web Crypto API (available in all modern browsers)
		Anvil.crypto = globalThis.crypto

		// Moment.js
		Anvil.moment = moment
		Anvil.moment.locale(Anvil.config.momentLocale)

		// Numeral.js
		Anvil.numeral = numeral
		for (let [ key, value ] of Object.entries(Anvil.config.numeralLocales)) {
			Anvil.numeral.register('locale', key, value)
		}
		Anvil.numeral.locale(Anvil.config.numeralLocale)

		// Compare versions
		Anvil.versions = versions

		// Session - use local storage to persist session data across browser sessions (session storage not used)
		Anvil.session = new Proxy(window.localStorage, {
			get (target, field) {
				return JSON.parse(target.getItem('session.' + field))
			},
			set (target, field, value) {
				target.setItem('session.' + field, JSON.stringify(value))
				return true
			},
			deleteProperty (target, field) {
				target.removeItem('session.' + field)
				return true
			}
		})

		// Local storage
		Anvil.store = new Proxy(window.localStorage, {
			get (target, field) {
				return JSON.parse(target.getItem(field))
			},
			set (target, field, value) {
				target.setItem(field, JSON.stringify(value))
				return true
			},
			deleteProperty (target, field) {
				target.removeItem(field)
				return true
			},
			clear: (target) => {
				target.clear()
				return true
			}
		})

		// Toast shortcuts
		Anvil.toast = {
			plain: (title, message, options = {}) => Anvil._toast({ title, message, options }),
			info: (title, message, options = {}) => Anvil._toast({ title, message, theme: 'info', options }),
			primary: (title, message, options = {}) => Anvil._toast({ title, message, theme: 'primary', options }),
			success: (title, message, options = {}) => Anvil._toast({ title, message, theme: 'success', options }),
			warning: (title, message, options = {}) => Anvil._toast({ title, message, theme: 'warning', options }),
			error: (title, message, options = {}) => Anvil._toast({ title, message, theme: 'danger', options })
		}

		// Load controllers
		await Anvil._autoLoad(Anvil.controller_map, controllerGlob, 'controllers')

		// Load models
		await Anvil._autoLoad(Anvil.model_map, modelGlob, 'models')

		// Load views
		await Anvil._autoLoad(Anvil.view_map, viewGlob, 'views')
	}

	_ensurePath (base, path_segments = []) {
		let ref = base
		for (let segment of path_segments) {
			if (typeof ref[segment] == 'undefined') ref[segment] = {}
			ref = ref[segment]
		}
		return ref
	}

	_setPath (base, path_segments = [], value = undefined) {
		if (path_segments.length == 0)
			throw new Error('Cannot set value at an empty path')

		let parent_ref = this._ensurePath(base, path_segments.slice(0, path_segments.length - 1))
		parent_ref[path_segments[path_segments.length - 1]] = value
	}

	async _autoLoad (map, glob_map, root) {

		console.log(`Loading modules under '${root}'`)
		const is_view = root === 'views'
		const ext = is_view ? '.ejs' : '.js'
		const prefix_re = new RegExp(`^\\.\/` + root + `\/`)
		const ext_re = new RegExp(`\\${ext}$`)

		for (const [glob_key, mod] of Object.entries(glob_map)) {

			// Convert e.g. './controllers/c_order/terminal.js' -> 'c_order/terminal'
			const rel = glob_key.replace(prefix_re, '').replace(ext_re, '')
			const segments = rel.split('/')
			const name = segments.pop()	// last segment is the module name

			// Replicate the manifest-era key formula:
			//   [path_segments.slice(1), name].join('/')
			// where slice(1) stripped the root category ('controllers' etc.).
			// JavaScript's Array.toString uses commas, so multi-level paths produce
			// keys like 'ui,form/text' — matching what ejs_include() looks up.
			const mod_key = segments.length > 0 ? [segments, name].join('/') : name

			// CJS compat layer wraps module.exports as the .default export.
			const ref = (mod.default !== undefined) ? mod.default : mod

			// Add controllers and models to the Anvil namespace hierarchy
			if (!is_view) {
				Anvil._ensurePath(Anvil, [root, ...segments])
				Anvil._setPath(Anvil, [root, ...segments, name], ref)
			}

			// Register in the module map
			map.set(mod_key, ref)
		}
	}

	// Initialize ANVIL - executed once after all assets have been loaded
	async init () {

		// Generate routing map
		for (const [key, controller] of Anvil.controller_map) {
			// Check each action if it is a page
			for (let action of Object.keys(controller)) {
				if (action.startsWith('page_')) {
					// Add this action automatically to the routing map
					Anvil.route_map.set(path.join(key, action.substring(5)), path.join(key, action))
				}
			}
		}

		// Handle routing when browser navigates
		$(window).on('popstate', (event) => {
			Anvil.route(window.location, false)
		})

		// Set default language active if localization is active
		/* TODO
		if (Anvil.config.localization) {
			Anvil.setLanguage(Anvil.config.defaultLanguage);
		}
		*/

		// Handle local links
		$('body').on('click', 'a', function(e) {

			// See if there is an href
			const href = $(this).attr('href')
			if (href === undefined) return

			// If the href is a hash link, do nothing
			if (href.startsWith('#')) return

			// Convert to a valid URL object
			const url = new URL(href, new URL(window.location).origin)

			// See if the URL is local (not pointing to another site)
			if (url.origin == new URL(window.location).origin) {
				// If so, route to it
				Anvil.route(url)
				e.preventDefault()
			}
		})

		// Initialize models
		for (const [key, model] of Anvil.model_map) {
			if (model && typeof model.init === 'function') await model.init()
		}

		// Initialize controllers
		for (const [key, controller] of Anvil.controller_map) {
			if (controller && typeof controller.init === 'function') await controller.init()
		}

		// Begin execution of the requested action (or the default one)
		Anvil.route(window.location, false)
	}

	/**
	 * Route the URL to a specific action, and run it (or the default action if none was given)
	 * @param {string|URL} url - The URL to be routed
	 */
	async route (url = '', push = true) {

		// Convert to a valid URL object
		if (typeof url == 'string') {
			url = new URL(url, new URL(window.location).origin)
		}

		// Extract path and params
		const action_path = url.pathname.substring(1)
		const search_params = new URLSearchParams(url.search)
		const params = {}
		for (const [key, value] of search_params) {
			params[key] = value
		}

		// Manipulate the browser history
		if (push === 'redirect') {
			window.history.replaceState({}, '', url)
		} else if (push === true) {
			window.history.pushState({}, '', url)
		}

		// Route
		if (action_path == '') {
			// Default action
			return await Anvil.exec(Anvil.config.defaultAction, params)
		} else if (Anvil.route_map.has(action_path)) {
			// Action found
			return await Anvil.exec(Anvil.route_map.get(action_path), params)
		} else {
			// Action not found
			// # Do nothing
		}
	}

	href (path, params) {
		return `${path}?${new URLSearchParams(params).toString()}`
	}

	/**
	 * Run an action within the specified controller
	 * @param {string|object} path - Path to the action like "cnt/act" or { 'controller': 'cnt', 'action': 'act' }
	 * @params {object} params - An object containing all the action function parameters
	 */
	async exec (path, ...params) {

		// Display spinner
		Anvil.showSpinner()

		try {

			// Did we get a path?
			if (!path) {
				console.warn('Path not specified')
				return
			}

			// Path reformat
			if (typeof path == 'string') {
				let p = path.split('/')
				path = {
					action: p.pop(),
					controller: p.join('/'),
				}
			}

			var ret = null

			// Check if the user is allowed to execute this action
			const controller = await Anvil.controller_map.get(path.controller)
			if (controller !== undefined && path.action in controller) {
				// We found the action

				// Check for authentication state (some controllers need authentication)
				if ('auth' in controller && controller.auth == 'required' && !Anvil.session.user_auth) {
					Anvil.route('/login', 'redirect')
					return
				}

				// Execute the action
				try {
					ret = await controller[path.action](...params)
				} catch (e) {
					/* All errors are meant to be handled within the executed action, however, any that is
					 * thrown into this scope will be ignored, and printed to console and for the user in
					 * the form of a toast.
					 */
					console.error(e)
					Anvil.toast.error('Hiba', e.message)
				}

			} else {
				// The controller or action is not to be found
				console.warn('Cannot find requested controller or action: ' + path.controller + '/' + path.action)
			}

			return ret
		} finally {
			// Once the callback has run, we remove the spinner
			Anvil.hideSpinner()
		}
	}

	ejs_include (template, template_data = {}) {

		// Resolve path
		let tmp_path = (this.from == '' || template.startsWith(path.sep))
			// Treat as absolute path if we are calling from root, or using an absolute reference
			? path.normalize(path.parse(template.startsWith(path.sep) ? template.substr(1) : template).dir)
			// Resolve relative paths
			: path.normalize(path.join(path.parse(this.from).dir, path.parse(template).dir))
		let tmp_name = path.parse(template).base
		let template_path = path.join(tmp_path, tmp_name)					// The virtual path
		let key = path.join(tmp_path.split(path.sep).join(','), tmp_name)	// Key in Anvil.view_map

		// Return rendered content
		return Anvil.view_map.get(key)(template_data, null, Anvil.ejs_include.bind({ from: template_path }))
	}

	/**
	 * Render html output and optionally inject it into the DOM
	 * @param {string} template - Name of the view template to be used
	 * @param {object} data - Variables for the template to act as a context
	 * @param {string} target - Id of the target element where we intend to insert the output. If it's value is empty (''), we do not insert anywhere.
	 * @param {boolean} append - append html code
	 * @param {promiseCallback} cb - Callback. Returned paramenters are err (error message if any) and str (html output) respectively.
	 */
	async render (template, template_data = {}, target = '#anvil-content', mode = null, cb) {

		if (template === null) {
			template = Anvil.config.defaultTemplate
		}

		// Let's begin by preparing the content
		let content = Anvil.ejs_include.bind({ from: '' })(template, template_data)

		/* Electron solution
		let content = await new Promise((resolve, reject) => {

			// Try to render the file
			ejs.renderFile(path.join(__dirname, 'views', template + '.ejs'), template_data, { root: 'src/app/views' }, function(err, str) {

				// Print error to console and cancel render
				if (err) {
					console.warn(err)
					reject(err)
					return
				}

				resolve(str)
			})
		})
		*/

		// If a target element was specified, inject the content into the DOM
		if (target !== '') {
			switch (mode) {
				case 'append':
					$(target).append(content)
					break

				case 'prepend':
					$(target).prepend(content)
					break

				case 'replace':
					// EVENT - Hook for modules that wish to process content before it is removed
					this.emit('before-content-removal', $(target).get(0))

					$(target).replaceWith(content)
					break

				default:
				case 'update':
					// EVENT - Hook for modules that wish to process content before it is removed
					this.emit('before-content-removal', $(target).get(0))

					$(target).html(content)
					break
			}

			// EVENT - Hook for modules that wish to further process the newly inserted content
			this.emit('after-content-render', $(target).get(0))
		}

		// An optional callback
		if (typeof cb == 'function') cb(content)

		return content
	}

	/*
	* Render template inside the default layout
	*/
	async renderPage (template, page_data = {}, child = false, cb) {

		if (child) {
			// If we are opening a child page, we hide the current one
			$('#anvil-content .anvil-page:last-child').hide()
		}

		// Backup the old page data, we may need this again
		let page_backup = Anvil.page

		// Render contents to default content holder
		try {
			// Set the page container
			Anvil.page = page_data

			// Attempt to render the new content
			var res = await Anvil.render(null, { content: { template: template, data: page_data } }, '#anvil-content', child ? 'append' : null, cb)

		} catch (err) {
			// Rendering failed, so we need to undo the reference changes, and try to act normal
			console.error(err)
			Anvil.page = page_backup
			return
		}

		if (child) {
			// If we are opening a child page, push the references into memory
			Anvil.pages.push(page_backup)
		} else {
			// We opened a new root page, so we can drop all cached pages
			Anvil.pages = []
		}

		// If there was a modal on the previous page, we destroy it
		if (Anvil.modal_open) {
			$('#' + page_backup.modal.modal_window_id).modal('hide')
		}

		// Scroll to the top
		window.scrollTo(0, 0)

		// If the page has an init function, call it after rendering
		if ('init' in Anvil.page) {
			await Anvil.page.init()
		}

		return res
	}

	popPage () {

		if (Anvil.pages.length == 0) {
			return Anvil.exec('main/action_home')
		}

		let target = $('#anvil-content .anvil-page:last-child')

		// EVENT - Hook for modules that wish to process content before it is removed
		this.emit('before-content-removal', $(target).get(0))

		// We remove the whole page
		target.remove()

		// Restore previous page
		Anvil.page = Anvil.pages.pop()
		$('#anvil-content .anvil-page:last-child').show()
	}

	setLanguage (code) {
		if (Object.keys(Anvil.languages).includes(code)) {
			Anvil.t = this.languages[code].translation
			Anvil.activeLanguage = code
		}
	}

	/**
	 * Get text from translation
	 * @param {string} key - Key of the translation entry (sections are dot separated)
	 * @param {string} [defaultText=null] - Default text to return if key is not found
	 * @param {string} [languageCode=activeLanguage] - Code of the translation language. If not set the current language will be used
	 * @returns {(null|string)} - Returns the translation entry if it is found; returns defaultText or null if not found
	 */
	/*
	text (key, defaultText, languageCode) {
		var code = languageCode ? languageCode : Anvil.activeLanguage
		return Anvil.Utils.deepGet(Anvil.languages[code].translation, key, defaultText)
	}
	*/

	showSpinner (job, progress = null) {
		Anvil._spinner_jobs++
		Anvil.setSpinner((typeof job == 'undefined' ? 'Az adatok előkészítése még folyamatban van' : job), progress)
		$('#anvil-spinner').addClass('loading')
	}

	setSpinner (job = null, progress = null) {
		// Update text
		if (job !== null) {
			$('#anvil-spinner-job').html(job)
		}
		// Update bar
		if (progress === null) {
			$('#anvil-spinner .progress-bar').addClass('progress-bar-striped progress-bar-animated').css({ width: '100%' })
		} else {
			let w = Math.max(0, Math.min(100, progress))
			let el = $('#anvil-spinner .progress-bar')
			if (w == 0) {
				el.addClass('no-transitions')
				el.removeClass('progress-bar-striped progress-bar-animated').css({ width: w + '%' })
				// Flush pending css changes
				el.get(0).offsetHeight
				el.removeClass('no-transitions')
			} else {
				el.removeClass('progress-bar-striped progress-bar-animated').css({ width: w + '%' })
			}
		}
	}

	hideSpinner () {
		if (Anvil._spinner_jobs > 0) Anvil._spinner_jobs--
		if (Anvil._spinner_jobs > 0) return
		$('#anvil-spinner').removeClass('loading')
	}

	async message (params) {

		return new Promise((resolve, reject) => {

			// Parameters
			var modal_window_id = 'anvil-modal-confirm-' + moment().valueOf()
			params.options = Object.assign({
				backdrop: 'static',
				keyboard: true,
				focus: true
			}, params.options)

			// Render modal html to DOM
			Anvil.render('ui/modal_message', {
				title: params.title ? params.title : 'TODO - APPNAME',
				message: params.message ? params.message : '',
				btn_ok: params.btn_ok ? params.btn_ok : '<i class="fas fa-check mr-2"></i>OK',
				modal_window_id: modal_window_id
			}, '#anvil-content', 'append', () => {

				// OK button was pressed, resolve with true
				$('#anvil-modal-confirm-ok-btn').click(function() { resolve(1) })

				// Initialize the modal window
				$('#'+modal_window_id).modal(params.options)

				// Provide a beforeclose callback
				if (('onbeforeclose' in params) && params.onbeforeclose) {
					$('#'+modal_window_id).on('hide.bs.modal', function () {
						if (jQuery.type(params.onbeforeclose) == 'function') return params.onbeforeclose(1)
						if (jQuery.type(params.onbeforeclose) == 'string') return window[params.onbeforeclose](1)
					});
					return true
				}

				// Provide a close callback
				$('#'+modal_window_id).on('hidden.bs.modal', function () {
					if (('onclose' in params) && params.onclose) {
						if (jQuery.type(params.onclose) == 'function') params.onclose(1)
						if (jQuery.type(params.onclose) == 'string') window[params.onclose](1)
					}

  					$(this).remove()
					Anvil.modal_open = false
				});

				// Show the modal
				Anvil.modal_open = true
				$('#'+modal_window_id).modal('show')
			})
		})
	}

	async confirm (params) {

		return new Promise((resolve, reject) => {

			// Make the modal appear
			var modal_window_id = 'anvil-modal-confirm-' + moment().valueOf();
			params.options = Object.assign({
				backdrop: 'static',
				keyboard: true,
				focus: true
			}, params.options);

			Anvil.render('ui/modal_confirm', {
				title: params.title ? params.title : 'TODO - APPNAME',
				message: params.message ? params.message : 'Are you sure?',
				btn_ok: params.btn_ok ? params.btn_ok : '<i class="fas fa-check mr-2"></i>OK',
				btn_ok_class: params.btn_ok_class ? params.btn_ok_class : 'primary',
				btn_cancel: params.btn_cancel ? params.btn_cancel : '<i class="fas fa-times mr-2"></i>CANCEL',
				modal_window_id: modal_window_id
			}, '#anvil-content', 'append', () => {
				$('#anvil-modal-confirm-ok-btn').click(() => {
					$('#' + modal_window_id).get(0).returnValue = 1
					$('#' + modal_window_id).modal('hide')
					resolve(1)
				})
				$('#anvil-modal-confirm-cancel-btn').click(() => {
					$('#' + modal_window_id).get(0).returnValue = 0
					$('#' + modal_window_id).modal('hide')
					resolve(0)
				})
				$('#'+modal_window_id).modal(params.options);

				if ('onbeforeclose' in params) {
					$('#'+modal_window_id).on('hide.bs.modal', function () {
						if (jQuery.type(params.onbeforeclose) == 'function') return params.onbeforeclose($(this).get(0).returnValue)
						if (jQuery.type(params.onbeforeclose) == 'string') return window[params.onbeforeclose]($(this).get(0).returnValue)
					})
					return true
				}

				$('#'+modal_window_id).on('hidden.bs.modal', function () {
					if ('onclose' in params) {
						if (jQuery.type(params.onclose) == 'function') params.onclose($(this).get(0).returnValue)
						if (jQuery.type(params.onclose) == 'string') window[params.onclose]($(this).get(0).returnValue)
					}

					$(this).remove()
					Anvil.modal_open = false
				});

				Anvil.modal_open = true
				$('#'+modal_window_id).modal('show');
			})
		} )
	}

	async modal (template, modal_data = {}, params = { options: {} }) {

		if (Anvil.modal_open) {
			Anvil.page.modal.close()
		}

		// Parameters
		var modal_window_id = 'anvil-modal-template-' + moment().valueOf()
		params.options = Object.assign({
			backdrop: 'static',
			keyboard: true,
			focus: true
		}, params.options)

		Anvil.page.modal = Object.assign(modal_data, {
			modal_window_id,
			close () {
				$('#' + Anvil.page.modal.modal_window_id).modal('hide')
			}
		})

		// Render modal html to DOM
		await Anvil.render('ui/modal', { modal_window_id, template, template_data: modal_data }, '#anvil-modal-container', 'append')

		// Initialize the modal window (bootstrap)
		$('#' + modal_window_id).modal(params.options)

		// Provide a beforeclose callback
		if (('onbeforeclose' in params) && params.onbeforeclose) {
			$('#' + modal_window_id).on('hide.bs.modal', function () {
				if (jQuery.type(params.onbeforeclose) == 'function') return params.onbeforeclose(1)
				if (jQuery.type(params.onbeforeclose) == 'string') return window[params.onbeforeclose](1)
			})

			return true
		}

		// Provide a close callback
		$('#' + modal_window_id).on('hidden.bs.modal', function () {
			if (('onclose' in params) && params.onclose) {
				if (jQuery.type(params.onclose) == 'function') params.onclose(1)
				if (jQuery.type(params.onclose) == 'string') window[params.onclose](1)
			}

			// EVENT - Hook for modules that wish to process content before it is removed
			Anvil.emit('before-content-removal', $(this).get(0))

			// Destroy
			$(this).remove()
			Anvil.modal_open = false
		});

		// Show the modal
		Anvil.modal_open = true
		$('#' + modal_window_id).modal('show')

		// If the modal has an init function, call it after rendering
		if ('init' in Anvil.page.modal) {
			await Anvil.page.modal.init()
		}
	}

	async _toast (params) {

		// Make the toast appear
		var toast_window_id = 'anvil-toast-' + moment().valueOf()
		var classes = ''

		params.options = Object.assign({
			animation: true,
			autohide: true,
			delay: 5000,
			clear: false
		}, params.options)

		if (('theme' in params) && params.theme) {
			classes = 'text-white bg-' + params.theme
		}

		// If instructed, clear all previous toasts
		if (params.clear) {
			$('#anvil-toast-wrapper div').toast('hide')
		}

		// Render the toast html
		await Anvil.render('ui/toast', {
			title: params.title ? params.title : '',
			message: params.message ? params.message : '',
			toast_window_id: toast_window_id,
			classes: classes
		}, '#anvil-toast-wrapper', 'prepend')

		// Make it work
		$('#'+toast_window_id)
			.toast(params.options)					// Init
			.on('hidden.bs.toast', function () {	// Remove html after it hides
				$(this).remove()
			})
			.toast('show')							// Make it appear
	}

	toggleFullscreen() {
		if (!document.fullscreenElement) {
			document.documentElement.requestFullscreen()
		} else {
			if (document.exitFullscreen) {
				document.exitFullscreen()
			}
		}
	}
}

// global function alias for Anvil.text
//var _ = Anvil.text;

export default Anvil